import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { chatService } from "@/services/chatService";
import App from "./App.tsx";
import "./index.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./context-providers/AuthProvider";
import { DevPersonaSwitcher } from "@/components/dev/DevPersonaSwitcher";
import { SessionProvider } from "./context-providers/SessionProvider";
import { FeedbackProvider } from "./context-providers/FeedbackProvider";
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import { AppErrorFallback } from "@/components/ui/AppErrorFallback";
import { supabase } from "@/lib/supabase";
import { PostHogProvider } from "posthog-js/react";
import { createDevErrorBoundary } from "@/lib/devErrorBoundary";
import { normalizeViteEnvString } from "@/lib/viteEnv";

const queryClient = new QueryClient();

const bugsnagApiKey = import.meta.env.VITE_APP_BUGSNAG_KEY?.trim();

let ErrorBoundary: React.ComponentType<{
  children: React.ReactNode;
  FallbackComponent: React.ComponentType<React.ComponentProps<typeof AppErrorFallback>>;
}>;

if (bugsnagApiKey) {
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    plugins: [new BugsnagPluginReact()],
    onError: async function (event) {
      const { data } = await supabase.auth.getSession();
      const user = data.session?.user;
      if (user) {
        event.setUser(user.id, user.email, user.user_metadata.name);
      }
    },
    enabledReleaseStages: ["production", "staging"],
  });
  ErrorBoundary = Bugsnag.getPlugin("react").createErrorBoundary(React);
} else {
  ErrorBoundary = createDevErrorBoundary(AppErrorFallback);
}

const posthogKey = normalizeViteEnvString(import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined);
const posthogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  person_profiles: "identified_only" as const,
};

const AppTree = () => (
  <TooltipProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SessionProvider>
            <FeedbackProvider>
              <App />
              <DevPersonaSwitcher />
              <Toaster />
              <Sonner />
            </FeedbackProvider>
          </SessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </TooltipProvider>
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={AppErrorFallback}>
      {posthogKey ? (
        <PostHogProvider apiKey={posthogKey} options={posthogOptions}>
          <AppTree />
        </PostHogProvider>
      ) : (
        <AppTree />
      )}
    </ErrorBoundary>
  </StrictMode>
);

declare global {
  interface Window {
    toggleWebhook: typeof chatService.toggleWebhook;
  }
}

window.toggleWebhook = chatService.toggleWebhook;
